import * as cx from "classnames/bind";
import { Footer, HomeContent, HomeMenu, LoginModal } from "components";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import NextNprogress from "nextjs-progressbar";
import React, { useState, useEffect, useRef } from "react";
import Head from "next/head";
import { useSession } from "hooks";
import dynamic from "next/dynamic";
import { hotjar } from "react-hotjar";
import s from "styles/Home.module.scss";
import Cookies from "js-cookie";
import store from "store2";

const LegalPopup = dynamic(() => import("../components/popups/LegalPopup.js"), {
  ssr: false,
});

const HomeLayout = ({ children, className, progressbar = true, ...props }) => {
  const [loginModalState, setLoginModalState] = useState({
    show: false,
    index: 0,
  });
  const [showLegal, setShowLegal] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [minimized, setMinimized] = useState(false);

  const cocktail = children?.props?.cocktail;
  const progressBar = !!progressbar;

  const { data: session, status, mutate } = useSession();
  const loading = status === "loading";
  const cocktailsSearchRef = useRef(null);
  const router = useRouter();
  const { asPath } = router;

  const setShowLoginModal = (show) => setLoginModalState({ index: 0, show });

  useEffect(() => {
    hotjar.initialize(1691264, 6);
  }, []);

  // 👇 Popup: show after 3s, but keep minimized state across pages
  useEffect(() => {
    const wasMinimized = store.session("promoPopupMinimized") === true;

    const timer = setTimeout(() => {
      setShowPopup(true);
      if (wasMinimized) {
        setMinimized(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    async function decodeToken() {
      const url = `${process.env.NEXT_PUBLIC_REST_API_URL}auth/decode`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Api-Key": process.env.NEXT_PUBLIC_API_KEY,
        },
        body: JSON.stringify(router.query),
      });
      const data = await response.json();
      if (data.password) {
        const response = await signIn("credentials", {
          email: router.query.email,
          password: data.password,
          redirect: false,
        });
        mutate();
        setShowLegal(!!!response.ok);
        Cookies.set("CookieConsent", response.ok);
      } else {
        setShowLegal(true);
      }
      store.session("inAppChecked", true);
    }

    if (router.isReady && status !== "loading") {
      if (router.query.token && router.query.email && status !== "loading") {
        store.session("inApp", true);
        const params = new URLSearchParams(router.query);
        params.delete("callbackUrl");
        params.delete("token");
        params.delete("email");
        router.replace(
          { pathname: router.pathname, query: params.toString() },
          undefined,
          { shallow: true },
        );
        decodeToken();
      } else {
        setShowLegal(!!!(session || {}).user);
        store.session("inAppChecked", true);
      }
    }
  }, [router.isReady, router.query, status]);

  return (
    <>
      <Head>
        <link
          rel="alternate"
          hrefLang="en"
          href={`https://cocktailclub.com${asPath}`}
        />
        <link
          rel="alternate"
          hrefLang="es"
          href={`https://cocktailclub.com/es${asPath}`}
        />
      </Head>

      {progressBar && <NextNprogress options={{ showSpinner: false }} />}

      <div className={cx("Home", s.Home, className)} {...props}>
        <HomeMenu
          session={session}
          setLoginModalState={setLoginModalState}
          setShowLoginModal={setShowLoginModal}
          cocktailsSearchRef={cocktailsSearchRef}
        />

        <HomeContent>
          {React.Children.toArray(children).map((child) =>
            React.cloneElement(child, {
              ...props,
              loading,
              session,
              setShowLoginModal,
              setLoginModalState,
              cocktailsSearchRef,
            }),
          )}
        </HomeContent>

        <Footer />
        {showLegal && <LegalPopup />}

        {/* ✅ POPUP with persisted minimized state */}
        {showPopup && (
          <div className={`${s.KitPopup} ${minimized ? s.minimized : ""}`}>
            {!minimized ? (
              <div>
                <button
                  type="button"
                  className={s.KitPopupClose}
                  onClick={() => {
                    setMinimized(true);
                    store.session("promoPopupMinimized", true);
                  }}
                >
                  <img alt="" src="/img/icons/close.svg" />
                </button>
                <div className={cx(s.KitPopupContent, "flex")}>
                  <div>
                    <div className={cx(s.KitPopupBadge)}>- 50%</div>
                    <img src="/img/kit1.jpg" />
                  </div>
                  <div className={cx(s.KitPopupText)}>
                    <h4>Final chance to buy the Cocktail Club kit!</h4>
                    <p>
                      Use code:&nbsp;<span>KIT50</span>
                    </p>
                    <a href="bar-set">Buy now - 50% off</a>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={s.KitPopupMinimizedBar}
                onClick={() => {
                  setMinimized(false);
                  store.session("promoPopupMinimized", false);
                }}
              >
                <>
                  Get 50% of bar kit with code:{" "}
                  <i>
                    <b>KIT50</b>
                  </i>
                </>
              </div>
            )}
          </div>
        )}
      </div>

      <LoginModal
        onClose={() => setShowLoginModal(false)}
        state={loginModalState}
        setLoginModalState={setLoginModalState}
        title="Login or Sign up"
        cocktail={cocktail}
      />
    </>
  );
};

export default HomeLayout;
